import dayjs from "dayjs";
import { Pencil, Trash2 } from "lucide-react";

const HEADERS = [
  "Type",
  "Name",
  "Details",
  "Note",
  "Active",
  "Created At",
  "Actions",
];

const ActiveBadge = ({ active }) => (
  <span
    className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${
      active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
    }`}
  >
    {active ? "Yes" : "No"}
  </span>
);

const DefaultBadge = () => (
  <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
    Default
  </span>
);

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return dayjs(dateString).format("YYYY-MM-DD HH:mm");
};

const renderBankInfo = (method) => {
  if (method.payment_method !== "bank_transfer") return <span className="text-gray-400">—</span>;

  const bankDetails = method.bank_transfer_detail || method.details || {};

  const fields = [
    { label: "Bank Name", value: bankDetails.bank_name },
    { label: "Bank Address", value: bankDetails.bank_address },
    { label: "SWIFT", value: bankDetails.swift },
    { label: "Beneficiary Name", value: bankDetails.beneficiary_name },
    { label: "Beneficiary Address", value: bankDetails.beneficiary_address },
    { label: "Account Number", value: bankDetails.account_number },
    { label: "IFSC Code", value: bankDetails.ifsc_code },
    { label: "IBAN", value: bankDetails.iban },
    { label: "Sort Code", value: bankDetails.sort_code },
    { label: "Country", value: bankDetails.country },
    { label: "State / Region", value: bankDetails.state_region },
    { label: "City", value: bankDetails.city },
    { label: "Postal Code", value: bankDetails.postal_code },
  ];

  const filtered = fields.filter((f) => f.value && String(f.value).trim() !== "");

  if (filtered.length === 0) return <span className="text-gray-400">—</span>;

  return (
    <div className="text-[11px] text-gray-700 space-y-0.5 text-left max-h-32 overflow-y-auto">
      {filtered.map((f, i) => (
        <div key={i}>
          <span className="font-medium text-gray-500">{f.label}:</span>{" "}
          <span className="text-gray-800">{f.value}</span>
        </div>
      ))}
    </div>
  );
};

const renderPaymentMethodName = (method) => {
  const type = method.payment_method;
  if (type === "bank_transfer")
    return method.bank_transfer_detail?.payment_method_name || "—";
  if (["paypal", "payoneer", "skrill"].includes(type)) {
    const detail = method[`${type}_detail`] || method.email_payment_detail;
    const email = detail?.email;
    const accountHolder = detail?.account_holder_name;
    if (!email) return "—";
    return (
      <div className="space-y-0.5">
        <div className="text-xs text-gray-900">{email}</div>
        {accountHolder && (
          <div className="text-[10px] text-gray-500">{accountHolder}</div>
        )}
      </div>
    );
  }
  if (type === "other")
    return method.other_payment_detail?.payment_method_name || "—";
  return "—";
};

const PaymentMethodList = ({ methods, onEdit, onDelete }) => {
  return (
    <div className="w-full bg-white shadow rounded-lg overflow-hidden">
      <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
        <table className="w-full table-auto border-collapse min-w-[1100px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              {HEADERS.map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {methods.length === 0 ? (
              <tr>
                <td
                  colSpan={HEADERS.length}
                  className="py-10 text-center text-gray-400 text-xs"
                >
                  No payment methods added yet.
                </td>
              </tr>
            ) : (
              methods.map((method, index) => {
                const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                return (
                  <tr
                    key={method.id}
                    className={`${rowBg} hover:bg-blue-50 transition-colors`}
                  >
                    <td className="px-3 py-2 text-xs text-center text-gray-900 capitalize align-top">
                      <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                        <span>{method.payment_method?.replace("_", " ")}</span>
                        {method.is_default && <DefaultBadge />}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-center text-gray-900 align-top">
                      {renderPaymentMethodName(method)}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700 align-top">
                      {renderBankInfo(method)}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700 align-top">
                      <div className="whitespace-pre-wrap max-h-24 overflow-y-auto text-left">
                        {method.note || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap text-center align-top">
                      <ActiveBadge active={method.active_flag} />
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500 align-top">
                      {formatDate(method.createdAt)}
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap align-top">
                      <div className="flex justify-center gap-3">
                        <Pencil
                          className="w-4 h-4 text-blue-400 hover:text-blue-600 cursor-pointer transition-colors"
                          title="Edit"
                          onClick={() => onEdit?.(method)}
                        />
                        <Trash2
                          className="w-4 h-4 text-red-400 hover:text-red-600 cursor-pointer transition-colors"
                          title="Delete"
                          onClick={() => onDelete?.(method.id)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentMethodList;
